import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import {FormattedMessage, injectIntl} from "react-intl";
import Select, {SelectOption} from "@amzn/meridian/select";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import {CONFIG_TYPE, CONFIG_TYPE_VALUE} from "../util/Constants";
import Box from "@amzn/meridian/box";
import React, {useContext} from "react";
import RefreshTimer from "../util/RefreshTimer";
import Tooltip from "@amzn/meridian/tooltip";
import Icon from "@amzn/meridian/icon";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import downloadSmallTokens from "@amzn/meridian-tokens/base/icon/download-small";
import {DraftContext} from "../draftSave/context/DraftSaveContext";
import {
    ADTFSorterConfigCSVParser,
    DivertConfigCSVParser,
    SorterConfigCSVParser,
    TestConfigCSVParser,
    ZancaSortTestConfigCSVParser,
    TesseractTestConfigCSVParser, TesseractStowConfigCSVParser
} from "../util/Parsers";
import {createDownloadableFile, getDownloadFileName} from "../util/Util";
import {GlobalStateContext} from "../global/context/GlobalStateContext";
import {getLocalizedMessage} from "../util/Localization";
import {ConditionalWrapping, tooltipWrapper} from "../util/ConditionalWrapping";

const renderDeviceSelectionRow = (props) => {
    const {
        selectedMac, setSelectedMac, setValueConfiguration, defaultSelection,
        configurationKeys, deployDraft, sortedDeviceList,
        isDeployDisabled, resetDraftData, isRollbackDisabled,
        rollbackClickHandler, isAuthorized, valueConfiguration, isSorterConfig, intl,
    } = props;
    return (
        <Box type="fill" spacingInset="medium">
            <Row alignmentHorizontal="justify" widths={["fill", '30%']} spacing="400">
                <Row>
                    <Text>
                        <FormattedMessage id="devicename.title" defaultMessage="Select Device"/>
                    </Text>
                    <Select
                        value={selectedMac}
                        onChange={(value) => {
                            setSelectedMac(value);
                            setValueConfiguration(defaultSelection);
                        }}
                        placeholder={<FormattedMessage id="devicename.title" defaultMessage="Select Device"/>}
                        size="small"
                        width={250}>
                        {sortedDeviceList.map((device) => (
                            <SelectOption key={device.mac} value={device.mac}
                                          label={device.name + " (" + device.mac + ")"}/>
                        ))}
                        {!sortedDeviceList.length &&
                        <Column alignmentVertical="center" spacing="100" spacingInset="100">
                            <Text alignment="center"> No devices available </Text>
                        </Column>
                        }
                    </Select>
                </Row>
                <Row alignmentHorizontal="end">
                    <Button
                        size="small"
                        disabled={isDeployDisabled}
                        onClick={deployDraft}
                    ><FormattedMessage id="actions.deploydraft" defaultMessage="Deploy Draft"/>
                    </Button>
                    <Button
                        size="small"
                        disabled={isDeployDisabled}
                        type="tertiary"
                        onClick={() => resetDraftData(configurationKeys, selectedMac)}
                    ><FormattedMessage id="actions.discarddraft" defaultMessage="Discard Draft"/></Button>
                    <ConditionalWrapping
                        condition={!isAuthorized && isSorterConfig}
                        wrapper={tooltipWrapper}
                        args={{
                            title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                            position: "top"
                        }}
                        child={<Button
                            size="small"
                            className="redBtn"
                            disabled={isRollbackDisabled || (!isAuthorized && (isSorterConfig || valueConfiguration === ""))}
                            onClick={rollbackClickHandler}
                        ><FormattedMessage id="actions.rollback" defaultMessage="Rollback"/></Button>}
                    />
                </Row>
            </Row>
        </Box>
    )
}

const renderConfigurationSelectionRow = (props) => {
    const {selectedMac, valueConfiguration, setValueConfiguration,
        configurationKeys, intl, scrollToBottom, refreshDeps,
        isDownloadDisabled, downloadConfigHandler} = props;
    return (
        selectedMac && <Row width="100%" widths={["60%", "40%"]}>
            <Row>
                <Text>
                    <FormattedMessage id="configtype.title" defaultMessage="Select Config Type"/>
                </Text>
                <Select
                    value={valueConfiguration}
                    onChange={setValueConfiguration}
                    disabled={!selectedMac}
                    placeholder={<FormattedMessage id="configtype.title" defaultMessage="Select Config Type"/>}
                    size="small"
                    width={200}>
                    {
                        configurationKeys.map((configKey) => {
                            return (
                                <SelectOption key={configKey} value={configKey} label={CONFIG_TYPE_VALUE[configKey]}/>
                            )
                        })
                    }
                </Select>
                <Button
                    size="small"
                    onClick={() => downloadConfigHandler(valueConfiguration, selectedMac)}
                    disabled={isDownloadDisabled(valueConfiguration, selectedMac)}
                >
                    <Icon tokens={downloadSmallTokens} />
                    <FormattedMessage id="config.download" defaultMessage="Download Config"/>
                </Button>
            </Row>
            <Row alignmentHorizontal="end">
                {valueConfiguration && <RefreshTimer deps={[...refreshDeps]}/>}
                <Tooltip position="top" title={intl.formatMessage({
                    id: 'tooltip.scrolltobottom',
                    defaultMessage: 'Scroll to bottom!'
                })}>
                    <Button onClick={scrollToBottom}
                            type="secondary"
                            size="small">
                        <Icon tokens={chevronDownLargeTokens}/>
                    </Button>
                </Tooltip>
            </Row>
        </Row>
    )
}

const getStringifiedData = (configType, data) => {
    if (configType === CONFIG_TYPE.SORTER_TEST_PACKAGE) {
        return TestConfigCSVParser.STRINGIFY(data);
    } else if (configType === CONFIG_TYPE.SORTER_DESTINATION) {
        return SorterConfigCSVParser.STRINGIFY(data);
    } else if (configType === CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE) {
        return TestConfigCSVParser.STRINGIFY(data);
    } else if (configType === CONFIG_TYPE.ADTF_SORTER_DESTINATION) {
        return ADTFSorterConfigCSVParser.STRINGIFY(data);
    } else if (configType === CONFIG_TYPE.DIVERT_CONFIGURATION) {
        return DivertConfigCSVParser.STRINGIFY(data);
    } else if (CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION === configType) {
        return ZancaSortTestConfigCSVParser.STRINGIFY(data);
    } else if (CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION === configType) {
        return TesseractTestConfigCSVParser.STRINGIFY(data);
    } else if (CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION === configType) {
        return TesseractStowConfigCSVParser.STRINGIFY(data);
    } else {
        return "";
    }
}

const ConfigurationTabHeader = (props) => {
    const {getFilteredDraftData} = useContext(DraftContext);
    const {showWarning} = useContext(GlobalStateContext);
    const deployDraft = () => {
        showWarning(props.intl.formatMessage({id: "device.editWarning"}), () => props.deployDraft());
    }
    const downloadConfigHandler = (stateKey, subKey) => {
        const data = getFilteredDraftData({stateKey, subKey});
        createDownloadableFile(getDownloadFileName(stateKey), getStringifiedData(stateKey, data));
    }
    const isDownloadDisabled = (stateKey, subKey) => {
        if(!stateKey || !subKey) return true;
        const data = getFilteredDraftData({stateKey, subKey});
        return data.length === 0;
    }
    return (
        <Column>
            {renderDeviceSelectionRow({ ...props, deployDraft })}
            {renderConfigurationSelectionRow({...props, downloadConfigHandler, isDownloadDisabled})}
        </Column>
    )
}

export default injectIntl(ConfigurationTabHeader)


// WEBPACK FOOTER //
// ./src/components/commonui/ConfigurationTabHeader.js